import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ChatMessage,
  Conversation,
  ConversationState,
  MessageDeliveryStatus,
} from '../types';
import {
  listConversations,
  getConversation,
  createConversation,
  sendMessage,
  loadMessages,
  markConversationRead,
  fetchUnreadCount,
} from '../actions';

const initialState: ConversationState = {
  conversations: [],
  totalDocs: 0,
  nextCursor: null,
  activeConversation: null,
  messages: [],
  messagesNextCursor: null,
  totalUnread: 0,
  loading: false,
  messagesLoading: false,
  sendingMessage: false,
};

const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    setActiveConversation: (
      state,
      action: PayloadAction<Conversation | null>,
    ) => {
      state.activeConversation = action.payload;
      state.messages = [];
      state.messagesNextCursor = null;
    },

    clearMessages: (state) => {
      state.messages = [];
      state.messagesNextCursor = null;
    },

    addOptimisticMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },

    updateMessageStatus: (
      state,
      action: PayloadAction<{
        tempId: string;
        status: MessageDeliveryStatus;
        serverMessage?: ChatMessage;
      }>,
    ) => {
      const { tempId, status, serverMessage } = action.payload;
      const idx = state.messages.findIndex((m) => m._tempId === tempId);
      if (idx !== -1) {
        if (serverMessage) {
          state.messages[idx] = {
            ...serverMessage,
            _status: status,
            _tempId: tempId,
          };
        } else {
          state.messages[idx]._status = status;
        }
      }
    },

    onNewConversation: (state, action: PayloadAction<Conversation>) => {
      const exists = state.conversations.some(
        (c) => c.id === action.payload.id,
      );
      if (!exists) {
        state.conversations.unshift(action.payload);
        state.totalDocs += 1;
      }
    },

    onNewMessage: (
      state,
      action: PayloadAction<{
        message: ChatMessage;
        unreadCount?: number;
        currentUserId?: string;
      }>,
    ) => {
      const { message, unreadCount, currentUserId } = action.payload;
      const isActiveConv =
        state.activeConversation?.id === message.conversationId;
      const isFromOther =
        String(message.senderUserId) !== String(currentUserId);

      // Update lastMessage on the conversation in the list
      const convIdx = state.conversations.findIndex(
        (c) => c.id === message.conversationId,
      );
      if (convIdx !== -1) {
        state.conversations[convIdx].lastMessage = message;
        state.conversations[convIdx].updatedAt = message.createdAt;

        // Set unreadCount from server value if:
        // - NOT viewing this conversation
        // - message is from another user
        if (!isActiveConv && isFromOther && unreadCount !== undefined) {
          state.conversations[convIdx].unreadCount = unreadCount;
        }

        // Move to top of list
        const [conv] = state.conversations.splice(convIdx, 1);
        state.conversations.unshift(conv);
      }

      // If this message belongs to the active conversation, add/replace in messages
      if (
        state.activeConversation &&
        message.conversationId === state.activeConversation.id
      ) {
        // Try to find existing message by uuid
        let matchIdx = -1;
        if (message.uuid) {
          matchIdx = state.messages.findIndex(
            (m) => m._tempId === message.uuid || m.uuid === message.uuid,
          );
        }

        // Try to find by id
        if (matchIdx === -1 && message.id) {
          matchIdx = state.messages.findIndex((m) => m.id === message.id);
        }

        // If from current user and still not found, try matching pending optimistic message
        if (matchIdx === -1 && !isFromOther) {
          matchIdx = state.messages.findIndex(
            (m) =>
              (m._status === 'sending' || m._status === 'sent') &&
              String(m.senderUserId) === String(currentUserId),
          );
        }

        if (matchIdx !== -1) {
          // Replace existing/optimistic message with server data
          state.messages[matchIdx] = {
            ...message,
            _status: 'delivered',
            _tempId: state.messages[matchIdx]._tempId,
          };
        } else if (isFromOther) {
          // Only add new message if it's from another participant
          state.messages.push({ ...message, _status: 'delivered' });
        }
      }
    },

    onConversationRead: (state, action: PayloadAction<Conversation>) => {
      const updatedConv = action.payload;

      // Only process if user is currently viewing this conversation
      if (
        state.activeConversation &&
        state.activeConversation.id === updatedConv.id
      ) {
        state.activeConversation.participants = updatedConv.participants;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ==================== List Conversations ====================
      .addCase(listConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(listConversations.fulfilled, (state, action) => {
        const { data, totalDocs, nextCursor, isLoadMore } = action.payload;
        if (isLoadMore) {
          state.conversations = [...state.conversations, ...(data || [])];
        } else {
          state.conversations = data || [];
        }
        state.totalDocs = totalDocs ?? state.totalDocs;
        state.nextCursor = nextCursor ?? null;
        state.loading = false;
      })
      .addCase(listConversations.rejected, (state) => {
        state.loading = false;
      })

      // ==================== Get Single Conversation ====================
      .addCase(getConversation.fulfilled, (state, action) => {
        state.activeConversation = action.payload?.data || action.payload;
      })

      // ==================== Create Conversation ====================
      .addCase(createConversation.fulfilled, (state) => {
        state.totalDocs += 1;
      })

      // ==================== Send Message ====================
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const serverMessage = action.payload?.data || action.payload;
        const uuid = action.payload?._uuid;

        if (uuid) {
          const idx = state.messages.findIndex((m) => m._tempId === uuid);
          if (idx !== -1) {
            // Don't downgrade from 'delivered' to 'sent' if socket arrived first
            const currentStatus = state.messages[idx]._status;
            if (currentStatus === 'delivered') return;

            // Only merge server data if it contains a valid message (has id + content)
            if (serverMessage?.id && serverMessage?.content) {
              state.messages[idx] = {
                ...serverMessage,
                _status: 'sent',
                _tempId: uuid,
              };
            } else {
              // API didn't return full message — just update status
              state.messages[idx]._status = 'sent';
              if (serverMessage?.id) {
                state.messages[idx].id = serverMessage.id;
              }
            }
          }
        }
      })
      .addCase(sendMessage.rejected, (state) => {
        state.sendingMessage = false;
      })

      // ==================== Load Messages ====================
      .addCase(loadMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(loadMessages.fulfilled, (state, action) => {
        const { data, nextCursor, isLoadMore } = action.payload;
        // BE returns messages DESC (newest first), reverse to oldest-first for chat UI
        const messages = [...(data || [])].reverse();
        if (isLoadMore) {
          // Prepend older messages at the beginning
          state.messages = [...messages, ...state.messages];
        } else {
          state.messages = messages;
        }
        state.messagesNextCursor = nextCursor ?? null;
        state.messagesLoading = false;
      })
      .addCase(loadMessages.rejected, (state) => {
        state.messagesLoading = false;
      })

      // ==================== Mark as Read ====================
      .addCase(markConversationRead.fulfilled, (state, action) => {
        const { id, userId, agencyId } = action.payload;
        const conv = state.conversations.find((c) => c.id === id);
        if (conv) {
          conv.unreadCount = 0;
          // Find current user's participant:
          // 1. by userId (direct participant)
          // 2. by agencyId (agency participant with userId=null)
          // 3. by isCompanyParentBroadcast (admin broadcast participant)
          const me = conv.participants?.find(
            (p) =>
              (p.userId && String(p.userId) === String(userId)) ||
              (!p.userId &&
                agencyId &&
                String(p.agencyId) === String(agencyId)) ||
              p.isCompanyParentBroadcast,
          );
          if (me) {
            me.lastReadAt = new Date().toISOString();
          }
        }
      })

      // ==================== Unread Count ====================
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        const data = action.payload?.data;
        if (typeof data === 'number') {
          state.totalUnread = data;
        } else {
          state.totalUnread =
            data?.totalUnreadConversations ?? data?.totalUnread ?? 0;
        }
      });
  },
});

export const messageReducer = messageSlice.reducer;
export const messageActions = {
  ...messageSlice.actions,
  listConversations,
  getConversation,
  createConversation,
  sendMessage,
  loadMessages,
  markConversationRead,
  fetchUnreadCount,
};
